"use client";

import React, { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ShopifyProduct } from "@/lib/shopify";
import { useCart } from "@/context/CartContext";

const benefits = [
  { label: "9g Protein" },
  { label: "4g Fiber" },
  { label: "Vegan" },
  { label: "Gluten-Free" },
  { label: "Chickpeas & Oats" },
];

const nutritionFacts = [
  { labelEn: "Total Fat", labelFr: "Lipides", value: "17g", percent: "21%", indent: false },
  { labelEn: "Saturated Fat", labelFr: "saturés", value: "8g", percent: "40%", indent: true },
  { labelEn: "Trans Fat", labelFr: "Gras trans", value: "0g", percent: null, indent: true },
  { labelEn: "Cholesterol", labelFr: "Cholestérol", value: "0mg", percent: "0%", indent: false },
  { labelEn: "Sodium", labelFr: "Sodium", value: "122mg", percent: "5%", indent: false },
  { labelEn: "Total Carbohydrate", labelFr: "Glucides", value: "35g", percent: null, indent: false },
  { labelEn: "Dietary Fibre", labelFr: "Fibres alimentaires", value: "4g", percent: "15%", indent: true },
  { labelEn: "Total Sugars", labelFr: "Sucres totaux", value: "19g", percent: "19%", indent: true },
  { labelEn: "Protein", labelFr: "Protéines", value: "9g", percent: null, indent: false },
];

export default function PurchasePage() {
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<ShopifyProduct | null>(null);
  const [selectedVariantId, setSelectedVariantId] = useState<string>("");
  const [quantity, setQuantity] = useState(1);
  const [ingredientsOpen, setIngredientsOpen] = useState(false);
  const [nutritionOpen, setNutritionOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [orderLoading, setOrderLoading] = useState(false);
  const [orderError, setOrderError] = useState<string | null>(null);
  const [addedToCart, setAddedToCart] = useState(false);
  const [isSwitzerlandUser, setIsSwitzerlandUser] = useState(false);

  const { addToCart } = useCart();

  // Detect Switzerland visitors — multi-layer strategy for maximum reliability
  useEffect(() => {
    async function detectCountry() {
      const CACHE_KEY = "pmu_geo_country";
      const CACHE_TTL = 1000 * 60 * 60 * 6; // 6 hours

      // Layer 1: Check localStorage cache (instant — no network call)
      try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          const { country, ts } = JSON.parse(cached) as { country: string; ts: number };
          if (Date.now() - ts < CACHE_TTL) {
            if (country === "CH") setIsSwitzerlandUser(true);
            return; // Cache hit — done
          }
        }
      } catch {
        // localStorage unavailable — continue
      }

      // Layer 2: Timezone hint — immediate, synchronous, no network
      // Europe/Zurich is the Swiss timezone; use as a fast pre-signal
      let timezoneHint = false;
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (tz === "Europe/Zurich" || tz === "Europe/Vaduz") {
          timezoneHint = true;
          setIsSwitzerlandUser(true); // Show CHF immediately while we confirm
        }
      } catch {
        // Intl not available
      }

      // Layer 3: Server-side geo route (most reliable — no CORS, uses real IP from headers)
      try {
        const res = await fetch("/api/geo", { signal: AbortSignal.timeout(5000) });
        if (res.ok) {
          const data = await res.json();
          const country: string = data.country ?? "UNKNOWN";
          const isCH = country === "CH";
          setIsSwitzerlandUser(isCH);
          try {
            localStorage.setItem(CACHE_KEY, JSON.stringify({ country, ts: Date.now() }));
          } catch { /* ignore */ }
          return; // Server route succeeded — done
        }
      } catch {
        // Server route failed — fall through to client-side APIs
      }

      // Layer 4: Race all client-side geo APIs in parallel (fallback)
      const clientApis: Promise<string | null>[] = [
        fetch("https://ipapi.co/json/", { signal: AbortSignal.timeout(5000) })
          .then((r) => r.json())
          .then((d) => (d?.country_code as string) ?? null)
          .catch(() => null),
        fetch("https://ipinfo.io/json?fields=country", { signal: AbortSignal.timeout(5000) })
          .then((r) => r.json())
          .then((d) => (d?.country as string) ?? null)
          .catch(() => null),
        fetch("https://api.country.is/", { signal: AbortSignal.timeout(5000) })
          .then((r) => r.json())
          .then((d) => (d?.country as string) ?? null)
          .catch(() => null),
      ];

      // Use the first successful result from any API
      const results = await Promise.allSettled(clientApis);
      let resolvedCountry: string | null = null;
      for (const result of results) {
        if (result.status === "fulfilled" && result.value) {
          resolvedCountry = result.value;
          break;
        }
      }

      if (resolvedCountry) {
        const isCH = resolvedCountry === "CH";
        setIsSwitzerlandUser(isCH);
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify({ country: resolvedCountry, ts: Date.now() }));
        } catch { /* ignore */ }
      } else if (!timezoneHint) {
        // All APIs failed and no timezone hint — default to non-CH (no change needed)
        setIsSwitzerlandUser(false);
      }
      // If timezoneHint was true but all APIs failed, keep CHF shown (better UX for Swiss users)
    }

    detectCountry();
  }, []);

  useEffect(() => {
    async function fetchProducts() {
      try {
        setLoading(true);
        setError(null);
        const res = await fetch("/api/shopify/products");
        const data = await res.json();

        if (!res.ok || data.error) {
          throw new Error(data.error || `HTTP ${res.status}`);
        }

        const loaded: ShopifyProduct[] = data.products ?? [];
        setProducts(loaded);

        if (loaded.length > 0) {
          setSelectedProduct(loaded[0]);
          const firstVariant = loaded[0].variants.edges[0]?.node;
          if (firstVariant) setSelectedVariantId(firstVariant.id);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load products");
      } finally {
        setLoading(false);
      }
    }
    fetchProducts();
  }, []);

  const selectedVariant = selectedProduct?.variants.edges.find(
    (e) => e.node.id === selectedVariantId
  )?.node;

  // CHF pricing map: keyed by variant title keywords (case-insensitive)
  const CHF_PRICES: Record<string, number> = {
    "4": 23.0,
    "12": 69.0,
  };

  function getChfPrice(variantTitle: string, productTitle: string): number | null {
    const combined = `${variantTitle} ${productTitle}`.toLowerCase();
    if (combined.includes("12")) return CHF_PRICES["12"];
    if (combined.includes("4")) return CHF_PRICES["4"];
    return null;
  }

  function getDisplayPrice(variant: { id: string; title: string; price: { amount: string; currencyCode: string } } | undefined): { amount: number; currency: string } {
    if (!variant) return { amount: 0, currency: "CAD" };
    if (isSwitzerlandUser) {
      const chfPrice = getChfPrice(variant.title, selectedProduct?.title ?? "");
      if (chfPrice !== null) return { amount: chfPrice, currency: "CHF" };
    }
    return { amount: parseFloat(variant.price.amount), currency: variant.price.currencyCode };
  }

  const displayPrice = getDisplayPrice(selectedVariant);
  const price = displayPrice.amount;
  const currencyCode = displayPrice.currency;
  const total = (price * quantity).toFixed(2);
  const productImage = selectedProduct?.images.edges[0]?.node;

  const handleOrderNow = async () => {
    if (!selectedVariant) return;
    setOrderLoading(true);
    setOrderError(null);

    try {
      const res = await fetch("/api/shopify/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          merchandiseId: selectedVariant.id,
          quantity,
        }),
      });

      const data = await res.json();

      if (!res.ok || data.error) {
        throw new Error(data.error || `Checkout failed (HTTP ${res.status})`);
      }

      if (!data.checkoutUrl) {
        throw new Error("No checkout URL returned. Please try again.");
      }

      // Log raw URL for debugging
      console.log("[purchase] raw checkoutUrl from API:", data.checkoutUrl);

      // Robust domain fix: handle relative paths and both absolute domain variants
      const rawUrl: string = data.checkoutUrl;
      let fixedUrl: string;

      if (rawUrl.startsWith("/")) {
        fixedUrl = `https://rxyjbi-i9.myshopify.com${rawUrl}`;
      } else {
        try {
          const url = new URL(rawUrl);
          if (
            url.hostname === "pickmeupsnacks.ca" ||
            url.hostname === "www.pickmeupsnacks.ca"
          ) {
            url.protocol = "https:";
            url.hostname = "rxyjbi-i9.myshopify.com";
          }
          fixedUrl = url.toString();
        } catch {
          fixedUrl = rawUrl;
        }
      }

      // Navigate directly — works in both iframe (Rocket preview) and normal browser
      window.location.href = fixedUrl;
    } catch (err) {
      setOrderError(err instanceof Error ? err.message : "Checkout failed. Please try again.");
      setOrderLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (!selectedProduct || !selectedVariant) return;
    addToCart(
      {
        id: selectedVariantId,
        variantId: selectedVariantId,
        label:
          selectedVariant.title === "Default Title"
            ? selectedProduct.title
            : `${selectedProduct.title} — ${selectedVariant.title}`,
        price: parseFloat(selectedVariant.price.amount),
        image: productImage?.url,
        imageAlt: productImage?.altText ?? selectedProduct.title,
      },
      quantity
    );
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };

  return (
    <main className="bg-pmu-cream min-h-screen">
      <Header />

      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-6 pt-28 pb-2">
        <nav className="flex items-center gap-2 text-sm text-pmu-taupe">
          <Link href="/homepage" className="hover:text-pmu-amber transition-colors">
            Home
          </Link>
          <span>/</span>
          <span className="text-pmu-espresso font-600">Shop</span>
        </nav>
      </div>

      {/* Loading State */}
      {loading && (
        <section className="max-w-7xl mx-auto px-6 py-20 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <div className="w-10 h-10 border-4 border-pmu-amber border-t-transparent rounded-full animate-spin" />
            <p className="text-pmu-taupe text-sm uppercase tracking-widest">Loading products…</p>
          </div>
        </section>
      )}

      {/* Error State */}
      {error && !loading && (
        <section className="max-w-7xl mx-auto px-6 py-20 flex items-center justify-center">
          <div className="bg-white rounded-2xl border border-red-200 p-10 text-center max-w-md">
            <p className="text-red-600 font-700 text-base mb-2">Unable to load products</p>
            <p className="text-pmu-taupe text-sm mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="btn-primary"
            >
              Try Again
            </button>
          </div>
        </section>
      )}

      {/* Main Product Section */}
      {!loading && !error && selectedProduct && (
        <section className="max-w-7xl mx-auto px-6 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">

            {/* Product Image */}
            <div className="relative">
              <div
                className="relative rounded-2xl overflow-hidden shadow-card max-w-lg mx-auto lg:mx-0"
                style={{
                  background: "#FAFAF8",
                  border: "1px solid rgba(168,196,216,0.2)",
                  padding: "2.5rem 2rem",
                  minHeight: "480px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl" style={{ background: "#A8C4D8" }} />
                <div className="absolute top-5 left-5 z-10 bg-pmu-espresso text-pmu-cream text-xs font-800 px-3 py-1.5 rounded-md tracking-widest uppercase">
                  {selectedProduct.availableForSale ? "New" : "Sold Out"}
                </div>
                <div className="relative w-full z-10" style={{ height: "400px" }}>
                  {productImage ? (
                    <Image
                      src={productImage.url}
                      alt={productImage.altText ?? `${selectedProduct.title} product image`}
                      fill
                      className="object-contain drop-shadow-2xl"
                      style={{ filter: "drop-shadow(0 20px 40px rgba(0,0,0,0.18))" }}
                      priority
                    />
                  ) : (
                    <Image
                      src="/assets/images/c50a0fd0-fbf4-49ad-af31-6a07048cf85c-1773860930021.png"
                      alt="PICK ME UP Chocolate Chip Cookie packaged product"
                      fill
                      className="object-contain drop-shadow-2xl"
                      style={{ filter: "drop-shadow(0 20px 40px rgba(0,0,0,0.18))" }}
                      priority
                    />
                  )}
                </div>
              </div>

              {/* Benefit pills */}
              <div className="flex flex-wrap gap-2 mt-5 justify-center lg:justify-start">
                {benefits.map((b) => (
                  <span
                    key={b.label}
                    className="flex items-center gap-1.5 bg-white border border-pmu-taupe-light/25 text-pmu-espresso text-xs font-600 px-3 py-1.5 rounded-md uppercase tracking-wide"
                  >
                    {b.label}
                  </span>
                ))}
              </div>
            </div>

            {/* Product Details */}
            <div className="flex flex-col gap-7">
              <div>
                <p className="text-pmu-amber font-700 text-xs uppercase tracking-widest mb-2" style={{ letterSpacing: "0.2em" }}>
                  Pick Me Up
                </p>
                <h1 className="font-fraunces text-4xl md:text-5xl text-pmu-espresso leading-tight mb-3">
                  {selectedProduct.title}
                </h1>
                <p className="text-pmu-taupe text-base leading-relaxed">
                  {selectedProduct.description ||
                    "Discipline never tasted so good. Introducing the ultimate Pick Me Up with 9g of plant-based protein and 4g of fiber. Made from wholesome chickpeas and oats all while having the familiar flavours from your childhood chocolate chip cookies. Pick Me Up cookies are proof that you can do your steps and have your cookie."}
                </p>
              </div>

              {/* Multiple products selector */}
              {products.length > 1 && (
                <div>
                  <p className="text-pmu-espresso font-700 text-xs uppercase tracking-widest mb-3" style={{ letterSpacing: "0.15em" }}>
                    Products
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {products.map((product) => (
                      <button
                        key={product.id}
                        onClick={() => {
                          setSelectedProduct(product);
                          const firstVariant = product.variants.edges[0]?.node;
                          if (firstVariant) setSelectedVariantId(firstVariant.id);
                        }}
                        className={`px-4 py-2 rounded-lg border text-sm font-600 transition-all ${
                          selectedProduct.id === product.id
                            ? "border-pmu-amber bg-pmu-amber-pale text-pmu-espresso" :"border-pmu-taupe-light/30 bg-white text-pmu-taupe hover:border-pmu-amber/40"
                        }`}
                      >
                        {product.title}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Variant / Size Options */}
              {selectedProduct.variants.edges.length > 0 && (
                <div>
                  <p className="text-pmu-espresso font-700 text-xs uppercase tracking-widest mb-3" style={{ letterSpacing: "0.15em" }}>
                    Choose Your Size
                  </p>
                  <div className="flex flex-col gap-3">
                    {selectedProduct.variants.edges.map(({ node: variant }) => {
                      const vDisplay = getDisplayPrice(variant);
                      return (
                      <button
                        key={variant.id}
                        onClick={() => setSelectedVariantId(variant.id)}
                        className={`relative flex items-center justify-between p-5 rounded-xl border transition-all duration-200 text-left ${
                          selectedVariantId === variant.id
                            ? "border-pmu-amber bg-pmu-amber-pale" :"border-pmu-taupe-light/30 bg-white hover:border-pmu-amber/40"
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <div
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                              selectedVariantId === variant.id
                                ? "border-pmu-amber bg-pmu-amber" :"border-pmu-taupe-light"
                            }`}
                          >
                            {selectedVariantId === variant.id && (
                              <div className="w-2 h-2 rounded-full bg-white" />
                            )}
                          </div>
                          <div>
                            <p className="font-700 text-pmu-espresso text-base">
                              {variant.title === "Default Title" ? selectedProduct.title : variant.title}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <span className="font-800 text-pmu-espresso text-xl">
                            {vDisplay.currency} ${vDisplay.amount.toFixed(2)}
                          </span>
                        </div>
                      </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Quantity Selector */}
              <div>
                <p className="text-pmu-espresso font-700 text-xs uppercase tracking-widest mb-3" style={{ letterSpacing: "0.15em" }}>
                  Quantity
                </p>
                <div className="flex items-center gap-4">
                  <div className="flex items-center bg-white border border-pmu-taupe-light/30 rounded-xl overflow-hidden">
                    <button
                      onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                      className="w-12 h-12 flex items-center justify-center text-pmu-espresso hover:bg-pmu-amber-pale transition-colors text-xl font-700"
                      aria-label="Decrease quantity"
                    >
                      −
                    </button>
                    <span className="w-12 text-center font-800 text-pmu-espresso text-lg">
                      {quantity}
                    </span>
                    <button
                      onClick={() => setQuantity((q) => q + 1)}
                      className="w-12 h-12 flex items-center justify-center text-pmu-espresso hover:bg-pmu-amber-pale transition-colors text-xl font-700"
                      aria-label="Increase quantity"
                    >
                      +
                    </button>
                  </div>
                  <div className="text-pmu-taupe text-sm">
                    Total:{" "}
                    <span className="text-pmu-espresso font-800 text-lg">
                      {currencyCode} ${total}
                    </span>
                  </div>
                </div>
              </div>

              {/* Order Error */}
              {orderError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
                  <p className="text-red-600 text-xs text-center">{orderError}</p>
                </div>
              )}

              {/* Add to Cart + Order Now Buttons */}
              <div className="flex flex-col gap-3">
                <button
                  onClick={handleAddToCart}
                  disabled={!selectedProduct.availableForSale || !selectedVariant}
                  className={`w-full flex items-center justify-center gap-2 py-4 px-6 rounded-xl border-2 font-700 text-base transition-all duration-200 ${
                    addedToCart
                      ? "border-green-500 bg-green-50 text-green-700" :"border-pmu-amber bg-white text-pmu-espresso hover:bg-pmu-amber-pale"
                  } ${!selectedProduct.availableForSale ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  {addedToCart ? (
                    <>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                      Added to Cart!
                    </>
                  ) : (
                    <>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
                        <line x1="3" y1="6" x2="21" y2="6" />
                        <path d="M16 10a4 4 0 01-8 0" />
                      </svg>
                      Add to Cart
                    </>
                  )}
                </button>

                {/* Order Now Button */}
                <button
                  onClick={handleOrderNow}
                  disabled={!selectedProduct.availableForSale || orderLoading || !selectedVariant}
                  className={`btn-primary w-full justify-center text-base py-4 ${
                    orderLoading || !selectedProduct.availableForSale ? "opacity-70 cursor-not-allowed" : ""
                  }`}
                >
                  {orderLoading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                      Redirecting to Checkout…
                    </>
                  ) : (
                    <>
                      {selectedProduct.availableForSale ? "Order Now" : "Out of Stock"}
                      {selectedProduct.availableForSale && (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path d="M5 12h14M12 5l7 7-7 7" />
                        </svg>
                      )}
                    </>
                  )}
                </button>
              </div>

              {/* Trust signals */}
              <div className="flex flex-wrap gap-4 text-sm text-pmu-taupe">
                <span className="flex items-center gap-1.5">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </svg>
                  Secure Checkout
                </span>
                {isSwitzerlandUser ? (
                  <span className="flex items-center gap-1.5 text-green-700 font-700">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="1" y="3" width="15" height="13" rx="2" />
                      <path d="M16 8h4l3 5v3h-7V8z" />
                      <circle cx="5.5" cy="18.5" r="2.5" />
                      <circle cx="18.5" cy="18.5" r="2.5" />
                    </svg>
                    Free Shipping
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="1" y="3" width="15" height="13" rx="2" />
                      <path d="M16 8h4l3 5v3h-7V8z" />
                      <circle cx="5.5" cy="18.5" r="2.5" />
                      <circle cx="18.5" cy="18.5" r="2.5" />
                    </svg>
                    Fast Shipping
                  </span>
                )}
                <span className="flex items-center gap-1.5">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                  Satisfaction Guaranteed
                </span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Nutrition Facts & Ingredients */}
      <section className="max-w-7xl mx-auto px-6 pb-16 grid md:grid-cols-2 gap-6">

        {/* Nutrition Facts Panel */}
        <div className="bg-white rounded-2xl shadow-card border border-pmu-taupe-light/20 overflow-hidden">
          <button
            onClick={() => setNutritionOpen(!nutritionOpen)}
            className="w-full flex items-center justify-between px-8 py-6 text-left hover:bg-pmu-amber-pale/50 transition-colors"
            aria-expanded={nutritionOpen}
          >
            <span className="font-700 text-pmu-espresso text-base uppercase tracking-wide">
              Nutrition Facts / Valeur nutritive
            </span>
            <svg
              width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
              className={`text-pmu-amber transition-transform duration-300 ${nutritionOpen ? "rotate-180" : ""}`}
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
          <div className={`overflow-hidden transition-all duration-400 ${nutritionOpen ? "max-h-[900px] opacity-100" : "max-h-0 opacity-0"}`}>
            <div className="px-8 pb-8 border-t border-pmu-espresso pt-0">
              <div className="border-2 border-pmu-espresso rounded-xl overflow-hidden mt-4 font-mono">
                <div className="bg-pmu-espresso text-pmu-cream px-4 py-3">
                  <p className="text-2xl font-900 leading-none tracking-tight">Nutrition Facts</p>
                  <p className="text-xl font-900 leading-none tracking-tight mt-0.5">Valeur nutritive</p>
                  <p className="text-xs mt-1 opacity-80">Per 1 cookie / Par 1 biscuit (75g)</p>
                </div>
                <div className="border-b-8 border-pmu-espresso px-4 py-3 flex items-end justify-between bg-white">
                  <div>
                    <p className="text-xs text-pmu-taupe font-600">Amount Per Serving / Teneur par portion</p>
                    <p className="text-5xl font-900 text-pmu-espresso leading-none">350</p>
                    <p className="text-sm font-700 text-pmu-espresso">Calories</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-pmu-taupe font-700">% Daily Value*</p>
                    <p className="text-xs text-pmu-taupe font-700">% valeur quotidienne*</p>
                  </div>
                </div>
                <div className="bg-white">
                  {nutritionFacts.map((item, i) => (
                    <div
                      key={i}
                      className={`flex items-center justify-between px-4 py-1.5 border-b border-gray-200 ${item.indent ? "pl-8" : ""} ${item.labelEn === "Protein" ? "border-b-4 border-pmu-espresso" : ""}`}
                    >
                      <span className={`text-sm text-pmu-espresso ${item.indent ? "font-400" : "font-700"}`}>
                        {item.labelEn} / {item.labelFr}{" "}
                        <span className="font-400">{item.value}</span>
                      </span>
                      {item.percent && (
                        <span className="text-sm font-700 text-pmu-espresso">{item.percent}</span>
                      )}
                    </div>
                  ))}
                </div>
                <div className="bg-white px-4 py-2 border-b border-gray-200 flex flex-col gap-1">
                  <div className="flex gap-4 flex-wrap">
                    <span className="text-xs text-pmu-taupe">Potassium <strong className="text-pmu-espresso">213mg</strong> <strong className="text-pmu-espresso">5%</strong></span>
                    <span className="text-xs text-pmu-taupe">Calcium <strong className="text-pmu-espresso">57mg</strong> <strong className="text-pmu-espresso">4%</strong></span>
                    <span className="text-xs text-pmu-taupe">Iron / Fer <strong className="text-pmu-espresso">1.5mg</strong> <strong className="text-pmu-espresso">8%</strong></span>
                  </div>
                </div>
                <div className="bg-white px-4 py-2">
                  <p className="text-xs text-pmu-taupe leading-snug">
                    * The % Daily Value tells you how much a nutrient in a serving of food contributes to a daily diet. 2,000 calories a day is used for general nutrition advice.
                  </p>
                  <p className="text-xs text-pmu-taupe leading-snug mt-1">
                    * La % valeur quotidienne vous indique dans quelle mesure un nutriment dans une portion d&apos;aliment contribue à un régime alimentaire quotidien. 2 000 calories par jour sont utilisées pour des conseils nutritionnels généraux.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Ingredients & Allergens */}
        <div className="bg-white rounded-2xl shadow-card border border-pmu-taupe-light/20 overflow-hidden">
          <button
            onClick={() => setIngredientsOpen(!ingredientsOpen)}
            className="w-full flex items-center justify-between px-8 py-6 text-left hover:bg-pmu-amber-pale/50 transition-colors"
            aria-expanded={ingredientsOpen}
          >
            <span className="font-700 text-pmu-espresso text-base uppercase tracking-wide">
              Ingredients & Allergens / Ingrédients et allergènes
            </span>
            <svg
              width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
              className={`text-pmu-amber transition-transform duration-300 ${ingredientsOpen ? "rotate-180" : ""}`}
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
          <div className={`overflow-hidden transition-all duration-400 ${ingredientsOpen ? "max-h-[900px] opacity-100" : "max-h-0 opacity-0"}`}>
            <div className="px-8 pb-8 border-t border-pmu-amber/10 pt-6 flex flex-col gap-6">
              <div>
                <h3 className="font-700 text-pmu-espresso text-sm mb-2 uppercase tracking-wider">Ingredients (English)</h3>
                <p className="text-pmu-taupe text-sm leading-relaxed">
                  ROLLED OATS, ALMOND BUTTER, CANE SUGAR, CHOCOLATE CHIPS (COCOA MASS, CANE SUGAR, COCOA BUTTER, COCOA POWDER), COCONUT OIL, CHICKPEA FLOUR, APPLESAUCE UNSWEETENED, PEA PROTEIN, EGG REPLACEMENT (POTATO STARCH, TAPIOCA FLOUR, PSYLLIUM HUSK FIBER), VANILLA, BAKING SODA, BAKING POWDER.
                </p>
              </div>
              <div>
                <h3 className="font-700 text-pmu-espresso text-sm mb-2 uppercase tracking-wider">Ingrédients (Français)</h3>
                <p className="text-pmu-taupe text-sm leading-relaxed">
                  INGRÉDIENTS : FLOCONS D&apos;AVOINE, BEURRE D&apos;AMANDE, SUCRE, PÉPITES DE CHOCOLAT (PÂTE DE CACAO, SUCRE DE CANNE, BEURRE DE CACAO, POUDRE DE CACAO), HUILE DE COCO, FARINE DE POIS CHICHES, COMPOTE DE POMMES NON SUCRÉES, PROTÉINES DE POIS, SUBSTITUT D&apos;ŒUFS (FÉCULE DE POMME DE TERRE, FARINE DE TAPIOCA, FIBRE DE COQUE DE PSYLLIUM), VANILLE, BICARBONATE DE SOUDE, POUDRE À PATE.
                </p>
              </div>
              <div>
                <h3 className="font-700 text-pmu-espresso text-xs mb-3 uppercase tracking-widest">
                  Allergen Information / Informations sur les allergènes
                </h3>
                <div className="flex flex-col gap-3">
                  <div className="bg-pmu-amber-pale rounded-xl px-4 py-3 border border-pmu-amber/20">
                    <p className="text-pmu-espresso font-700 text-sm mb-0.5">Contains / Contient :</p>
                    <p className="text-pmu-taupe text-sm">Almond, Coconut / Amandes, Coco</p>
                  </div>
                  <div className="bg-pmu-cream rounded-xl px-4 py-3 border border-pmu-taupe-light/20">
                    <p className="text-pmu-espresso font-700 text-sm mb-0.5">May Contain / Peut contenir :</p>
                    <p className="text-pmu-taupe text-sm">Other Tree Nuts, Peanuts / Autres fruits à coque, Arachides</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
